import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLibrary } from '../../store/useLibrary';
import { WEIGHTS } from '../../domain/catalogs';
import { Button, Card, Field, inputClass, Modal, Segmented, useToast } from '../../components/ui';
import { Brush, Dropper, Eraser, Fill, Plus, Redo, Trash, Undo, Pencil, Arrow, Download } from '../../components/icons';
import { useEditor } from './useEditor';
import { ChartCanvas } from './ChartCanvas';
import { Loupe } from './Loupe';
import { YarnLink } from './YarnLink';
import { ExportModal } from './ExportModal';
import type { PatternFields } from '../../domain/types';

const META_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: 'designer', label: 'Designer', placeholder: 'Your name' },
  { key: 'tags', label: 'Tags', placeholder: 'comma, separated' },
  { key: 'needle', label: 'Needle size', placeholder: 'e.g. US 7 / 4.5mm' },
  { key: 'technique', label: 'Technique', placeholder: 'stranded / intarsia' },
];

export function EditorPage() {
  const { id } = useParams();
  const lib = useLibrary();
  const record = lib.get(id);
  const [cellPx, setCellPx] = useState(22);
  const [metaOpen, setMetaOpen] = useState(false);

  if (!record) {
    return (
      <div className="mx-auto max-w-md p-10 text-center">
        <p className="font-display text-2xl">Pattern not found</p>
        <Link to="/" className="mt-3 inline-block text-accent">
          ← Back to library
        </Link>
      </div>
    );
  }
  return <Editor key={record.id} recordId={record.id} cellPx={cellPx} setCellPx={setCellPx} metaOpen={metaOpen} setMetaOpen={setMetaOpen} />;
}

function Editor({
  recordId,
  cellPx,
  setCellPx,
  metaOpen,
  setMetaOpen,
}: {
  recordId: string;
  cellPx: number;
  setCellPx: (n: number) => void;
  metaOpen: boolean;
  setMetaOpen: (b: boolean) => void;
}) {
  const lib = useLibrary();
  const record = lib.get(recordId)!;
  const ed = useEditor(record);
  const { toast } = useToast();
  const [loupe, setLoupe] = useState<{ r: number; c: number; x: number; y: number } | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const tools = [
    { value: 'brush' as const, label: <Brush />, title: 'Brush' },
    { value: 'eyedrop' as const, label: <Dropper />, title: 'Pick' },
    { value: 'erase' as const, label: <Eraser />, title: 'Erase' },
  ];

  return (
    <div className="mx-auto max-w-[1320px] px-4 pb-16">
      {/* toolbar */}
      <div className="sticky top-2 z-30 mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface/80 p-2 backdrop-blur" style={{ boxShadow: 'var(--shadow)' }}>
        <Link to="/" title="Library">
          <Button variant="ghost" iconOnly>
            <Arrow style={{ transform: 'rotate(180deg)' }} />
          </Button>
        </Link>
        <Segmented value={ed.tool} onChange={ed.setTool} options={tools} ariaLabel="Tool" />
        <div className="flex gap-1">
          <Button variant="ghost" iconOnly title="Undo" disabled={!ed.canUndo} onClick={ed.undo}>
            <Undo />
          </Button>
          <Button variant="ghost" iconOnly title="Redo" disabled={!ed.canRedo} onClick={ed.redo}>
            <Redo />
          </Button>
        </div>
        <Button variant="ghost" onClick={ed.fill} title="Fill with active yarn">
          <Fill /> Fill
        </Button>
        <Button variant="ghost" onClick={ed.clear} title="Clear chart">
          <Trash /> Clear
        </Button>
        <div className="flex items-center gap-1 rounded-xl border border-border bg-surface-2 px-2 py-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-text-3">Grid</span>
          <Button variant="ghost" className="h-7 px-2" onClick={() => ed.resize(ed.cols + 1, ed.rows)} title="Add column">
            + Col
          </Button>
          <Button variant="ghost" className="h-7 px-2" onClick={() => ed.resize(ed.cols, ed.rows + 1)} title="Add row">
            + Row
          </Button>
        </div>
        <label className="flex items-center gap-1 rounded-xl border border-border bg-surface-2 px-2 py-1 text-sm">
          <input
            type="number"
            min={4}
            max={80}
            value={ed.cols}
            onChange={(e) => ed.resize(+e.target.value, ed.rows)}
            className="w-12 bg-transparent text-center font-mono outline-none"
            aria-label="Stitches wide"
          />
          <span className="text-text-3">×</span>
          <input
            type="number"
            min={4}
            max={80}
            value={ed.rows}
            onChange={(e) => ed.resize(ed.cols, +e.target.value)}
            className="w-12 bg-transparent text-center font-mono outline-none"
            aria-label="Rows tall"
          />
        </label>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="range"
            min={12}
            max={40}
            value={cellPx}
            onChange={(e) => setCellPx(+e.target.value)}
            aria-label="Zoom"
            className="w-28 accent-[var(--accent)]"
          />
          <select
            value={ed.gauge}
            onChange={(e) => ed.setGauge(e.target.value)}
            className="rounded-xl border border-border bg-surface-2 px-2 py-1.5 text-sm"
            title="Yarn weight / gauge"
          >
            {WEIGHTS.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name} · {w.g} sts/4in
              </option>
            ))}
          </select>
          <Button variant="primary" onClick={() => setExportOpen(true)} title="Export this chart">
            <Download /> Export
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* canvas */}
        <Card className="overflow-hidden p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <button className="group flex items-center gap-2 text-left" onClick={() => setMetaOpen(true)} title="Edit name & details">
              <span className="font-display text-lg font-semibold">{record.name}</span>
              <Pencil className="opacity-0 transition group-hover:opacity-60" width={14} height={14} />
            </button>
            <span className="font-mono text-xs text-text-3">
              {ed.cols} sts × {ed.rows} rows · {ed.stats.gaugeName}
            </span>
          </div>
          <div className="overflow-auto rounded-lg bg-surface-2 p-3">
            <div className="w-max">
              <ChartCanvas editor={ed} cellPx={cellPx} onOpenCell={(r, c, x, y) => setLoupe({ r, c, x, y })} />
            </div>
          </div>
        </Card>

        {/* panel */}
        <aside className="flex flex-col gap-4">
          <Card className="p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-3">Active yarn</h3>
            <div className="mb-3 flex items-center gap-3">
              <div className="h-11 w-11 flex-none rounded-xl border border-border-strong" style={{ background: ed.yarn(ed.activeYarn)?.hex }} />
              <div>
                <div className="font-display text-lg font-semibold">{ed.yarn(ed.activeYarn)?.name}</div>
                <div className="font-mono text-xs uppercase text-text-3">{ed.yarn(ed.activeYarn)?.hex}</div>
              </div>
            </div>
            <YarnLink editor={ed} />
            <div className="grid grid-cols-8 gap-1.5">
              {ed.palette.map((y) => (
                <button
                  key={y.id}
                  title={`${y.name} ${y.hex}`}
                  aria-label={`Select ${y.name}`}
                  onClick={() => {
                    ed.setActiveYarn(y.id);
                    ed.setTool('brush');
                  }}
                  className="aspect-square rounded-lg border border-black/20"
                  style={{ background: y.hex, outline: y.id === ed.activeYarn ? '2px solid var(--accent)' : undefined, outlineOffset: 2 }}
                />
              ))}
              <label className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-border-strong text-text-3 hover:text-accent" title="Add custom colour">
                <Plus width={14} height={14} />
                <input type="color" className="sr-only" onChange={(e) => ed.addCustomColor(e.target.value)} />
              </label>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-text-3">
              Yarn key <span className="font-mono text-accent">{ed.stats.legend.length} colors</span>
            </h3>
            {ed.stats.legend.length === 0 ? (
              <p className="text-sm italic text-text-3">No yarn placed yet — paint a stitch to begin.</p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {ed.stats.legend.map((e) => (
                  <div key={e.id} className="flex items-center gap-2.5 text-sm">
                    <span className="h-4 w-4 flex-none rounded border border-black/30" style={{ background: e.yarn!.hex }} />
                    <span className="flex-1 truncate">{e.yarn!.name}</span>
                    <span className="font-mono text-xs text-text-3">{e.count}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-3">Swatch stats</h3>
            <div className="grid grid-cols-2 gap-2.5">
              <Stat value={ed.stats.painted} label="Stitches worked" />
              <Stat value={`${ed.stats.pct}%`} label="Chart filled" />
              <div className="col-span-2 rounded-xl border border-border bg-surface-2 p-2.5">
                <div className="font-mono text-xl font-bold text-accent">≈ {ed.stats.widthIn} × {ed.stats.heightIn} in</div>
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-text-3">Finished @ {ed.stats.gaugeName}</div>
              </div>
            </div>
          </Card>
        </aside>
      </div>

      {loupe && <Loupe editor={ed} cell={{ r: loupe.r, c: loupe.c }} anchor={{ x: loupe.x, y: loupe.y }} onClose={() => setLoupe(null)} />}
      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} editor={ed} record={record} />
      <MetaModal open={metaOpen} onClose={() => setMetaOpen(false)} recordId={recordId} onSaved={() => toast('Details saved')} />
    </div>
  );
}

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface-2 p-2.5">
      <div className="font-mono text-xl font-bold text-accent">{value}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-text-3">{label}</div>
    </div>
  );
}

function MetaModal({ open, onClose, recordId, onSaved }: { open: boolean; onClose: () => void; recordId: string; onSaved: () => void }) {
  const lib = useLibrary();
  const rec = lib.get(recordId);
  const [name, setName] = useState(rec?.name ?? '');
  const [desc, setDesc] = useState(rec?.description ?? '');
  const [fields, setFields] = useState<PatternFields>(rec?.fields ?? {});
  if (!open || !rec) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pattern details"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void lib.update(recordId, { name: name.trim() || 'Untitled pattern', description: desc.trim(), fields });
              onSaved();
              onClose();
            }}
          >
            Save details
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="Pattern name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className={inputClass} rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
        </Field>
        {META_FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            <input
              className={inputClass}
              placeholder={f.placeholder}
              value={fields[f.key] ?? ''}
              onChange={(e) => setFields((cur) => ({ ...cur, [f.key]: e.target.value }))}
            />
          </Field>
        ))}
      </div>
    </Modal>
  );
}
